/**
 * src/lib/i18n/I18nContext.jsx — Lightweight i18n for AC Nautitech
 *
 * Provides t() function via React context.
 * Locale persisted per user in localStorage ('acn_locale').
 * Supports dot-path keys and {placeholder} interpolation.
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { LOCALES, DEFAULT_LOCALE, translate } from './translate';

const STORAGE_KEY = 'acn_locale';

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LOCALE;
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_LOCALE;
  });

  const setLocale = useCallback((newLocale) => {
    const loc = LOCALES[newLocale] ? newLocale : DEFAULT_LOCALE;
    setLocaleState(loc);
    localStorage.setItem(STORAGE_KEY, loc);
  }, []);

  const t = useCallback((key, params) => translate(locale, key, params), [locale]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * Hook to access translation function and locale.
 * const { t, locale, setLocale } = useT();
 */
/**
 * localizedLabel(row, locale) — pull `label_${locale}` from a DB lookup row
 * (stages, project_types, etc.) with English fallback. FIX[T14]: stages/types
 * now render via DB labels instead of hardcoded JS strings (Phase 73-02 / AC-3).
 */
export function localizedLabel(row, locale = DEFAULT_LOCALE, fallback = '') {
  if (!row) return fallback;
  const key = `label_${locale}`;
  return row[key] || row.label_en || row.label_es || row.stage_name || row.slug || fallback;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback for components rendered outside provider (e.g. Login)
    return {
      locale: DEFAULT_LOCALE,
      setLocale: () => {},
      t: (key, params) => translate(DEFAULT_LOCALE, key, params),
    };
  }
  return ctx;
}
